import { useEffect, useState, useCallback, useRef, memo } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API, useTheme, useAuth } from "@/App";
import { asArray } from "@/lib/arrays";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  TrendingUp, TrendingDown, Clock, Target, Shield, Zap, Search,
  ArrowUpRight, ArrowDownRight, Activity, BarChart3, RefreshCw,
  Wifi, WifiOff, Send, ExternalLink, HandCoins,
  CheckCircle2, AlertTriangle, Trophy, AlertCircle, Wallet, Link2, Bell, BellOff, Calculator, Trash2,
  Lock, Sparkles, ChevronRight, RotateCcw, SlidersHorizontal
} from "lucide-react";
import { toast } from "sonner";
import { ConnectDhanModal, TradeExecutionModal, AlertInfoModal, DhanPortfolioWidget } from "@/components/dhan/DhanModals";
import DrishtiPaywallGate from "@/components/drishti/DrishtiPaywallGate";
import { useAlertNotifications } from "@/hooks/useAlertNotifications";
import EditAlertModal from "@/components/drishti/EditAlertModal";
import CloseTradeModal from "@/components/drishti/CloseTradeModal";

// ── fmt helpers ───────────────────────────────────────────────────────────
const fmt = (n) => n == null ? "—" : Number(n).toLocaleString("en-IN", { maximumFractionDigits: 0 });
const fmtDec = (n) => n == null ? "—" : Number(n).toLocaleString("en-IN", { maximumFractionDigits: 2 });

// ── Trading Capacity Widget — shown to regular users ──────────────────────
const TIER_CONFIG = {
  READY:         { color: "#00FF88", bg: "rgba(0,255,136,0.07)",  border: "rgba(0,255,136,0.20)",  icon: CheckCircle2, label: "Ready to Trade",        dot: "bg-[#00FF88]" },
  LOW_CAPITAL:   { color: "#FFCC00", bg: "rgba(255,204,0,0.06)",  border: "rgba(255,204,0,0.20)",  icon: AlertTriangle, label: "Low Capital",           dot: "bg-[#FFCC00]" },
  VERY_LOW:      { color: "#FF3B30", bg: "rgba(255,59,48,0.06)",  border: "rgba(255,59,48,0.20)",  icon: AlertCircle,   label: "Insufficient Capital",  dot: "bg-[#FF3B30]" },
  NOT_CONNECTED: { color: "#94A3B8", bg: "rgba(148,163,184,0.05)", border: "rgba(148,163,184,0.15)", icon: Link2,         label: "Not Connected",        dot: "bg-[#94A3B8]" },
};

function TradingCapacityWidget({ onConnectClick }) {
  const { theme } = useTheme();
  const isLight = theme === "light";
  const [cap, setCap] = useState(null);       // capacity data from API
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  const fetchCapacity = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/trading/trading-capacity`, { withCredentials: true });
      setCap(res.data);
    } catch (err) {
      // If 401 / error, treat as not connected
      setCap({ connected: false, tier: "NOT_CONNECTED", available_capital: 0, message: "Connect your Dhan account to see trading capacity." });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchCapacity(); }, [fetchCapacity]);

  if (loading) {
    return (
      <div className="terminal-card rounded-xl p-4 animate-pulse flex items-center gap-3 mb-1">
        <div className="w-8 h-8 rounded-2xl bg-white/10" />
        <div className="flex-1 space-y-1.5">
          <div className="h-3 w-40 rounded bg-white/10" />
          <div className="h-2 w-56 rounded bg-white/5" />
        </div>
      </div>
    );
  }

  const tier = cap?.tier ?? "NOT_CONNECTED";
  const cfg = TIER_CONFIG[tier] ?? TIER_CONFIG.NOT_CONNECTED;
  const TierIcon = cfg.icon;
  const capital = cap?.available_capital ?? 0;
  const isConnected = cap?.connected;

  return (
    <div
      className="rounded-xl overflow-hidden border transition-all duration-300"
      style={{ backgroundColor: cfg.bg, borderColor: cfg.border }}
    >
      {/* ── Compact header row ─────────────────────────────────────────── */}
      <div
        className="flex items-center gap-3 px-4 py-3 cursor-pointer select-none"
        onClick={() => setExpanded(e => !e)}
      >
        {/* Icon */}
        <div
          className="w-9 h-9 rounded-2xl flex items-center justify-center shrink-0"
          style={{ backgroundColor: `${cfg.color}18`, border: `1px solid ${cfg.color}30` }}
        >
          <TierIcon className="w-4.5 h-4.5" style={{ color: cfg.color }} />
        </div>

        {/* Title + status */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-sm font-bold font-outfit ${isLight ? "text-gray-900" : "text-white"}`}>
              {isConnected ? "My Trading Capacity" : "Broker Not Connected"}
            </span>
            {/* Tier badge */}
            <span
              className="text-[9px] font-mono font-bold px-2 py-0.5 rounded-full border"
              style={{ color: cfg.color, borderColor: cfg.border, backgroundColor: cfg.bg }}
            >
              <span className={`inline-block w-1.5 h-1.5 rounded-full mr-1 ${cfg.dot} align-middle`} />
              {cfg.label}
            </span>
          </div>
          <p className="text-[10px] text-gray-400 dark:text-[#94A3B8] font-plex mt-0.5 truncate">
            {isConnected
              ? `₹${fmtDec(capital)} available  ·  ${(cap.max_risk_pct * 100).toFixed(0)}% risk rule  ·  ${(cap.max_cap_pct * 100).toFixed(0)}% max position`
              : "Connect Dhan to see your available capital and risk capacity"}
          </p>
        </div>

        {/* Right: Action or expand chevron */}
        <div className="flex items-center gap-2 shrink-0">
          {!isConnected ? (
            <Button
              size="sm"
              onClick={(e) => { e.stopPropagation(); onConnectClick?.(); }}
              className="h-7 text-[10px] font-bold font-outfit px-3 rounded-2xl"
              style={{ background: cfg.color, color: "#000" }}
            >
              <Wallet className="w-3 h-3 mr-1" /> Connect Dhan
            </Button>
          ) : (
            <>
              <Button
                size="sm"
                variant="ghost"
                onClick={(e) => { e.stopPropagation(); fetchCapacity(); }}
                className="h-7 w-7 p-0 text-gray-400 hover:text-white rounded-2xl"
              >
                <RefreshCw className="w-3.5 h-3.5" />
              </Button>
              <span className={`text-gray-400 transition-transform duration-200 ${expanded ? "rotate-180" : ""}`}
                style={{ fontSize: 10, display: "inline-block" }}>▼</span>
            </>
          )}
        </div>
      </div>

      {/* ── Expanded detail panel ──────────────────────────────────────── */}
      {isConnected && expanded && (
        <div className="px-4 pb-4 pt-0 border-t" style={{ borderColor: cfg.border }}>
          <div className="pt-3 grid grid-cols-2 sm:grid-cols-4 gap-2">

            {/* Available capital */}
            <div className="bg-white/[0.03] border border-white/[0.07] rounded-xl p-3 text-center font-mono">
              <p className="text-[9px] uppercase text-gray-400 mb-1">Available Capital</p>
              <p className={`text-lg font-black ${isLight ? "text-gray-900" : "text-white"}`}>
                ₹{fmt(capital)}
              </p>
            </div>

            {/* Max risk per trade */}
            <div className="bg-[#FF3B30]/[0.05] border border-[#FF3B30]/20 rounded-xl p-3 text-center font-mono">
              <p className="text-[9px] uppercase text-[#FF3B30] mb-1">Max Risk / Trade</p>
              <p className="text-lg font-black text-[#FF3B30]">₹{fmt(cap.max_risk_per_trade)}</p>
              <p className="text-[8px] text-gray-400 mt-0.5">{(cap.max_risk_pct * 100).toFixed(0)}% of capital</p>
            </div>

            {/* Max position size */}
            <div className="bg-[#00F0FF]/[0.04] border border-[#00F0FF]/20 rounded-xl p-3 text-center font-mono">
              <p className="text-[9px] uppercase text-[#00F0FF] mb-1">Max Position Size</p>
              <p className="text-lg font-black text-[#00F0FF]">₹{fmt(cap.max_position_size)}</p>
              <p className="text-[8px] text-gray-400 mt-0.5">{(cap.max_cap_pct * 100).toFixed(0)}% of capital</p>
            </div>

            {/* What this means */}
            <div
              className="rounded-xl p-3 text-center font-mono col-span-2 sm:col-span-1 flex flex-col items-center justify-center gap-1"
              style={{ backgroundColor: cfg.bg, border: `1px solid ${cfg.border}` }}
            >
              <TierIcon className="w-5 h-5 mb-0.5" style={{ color: cfg.color }} />
              <p className="text-[9px] font-bold uppercase" style={{ color: cfg.color }}>{cfg.label}</p>
              <p className="text-[8px] text-gray-400 leading-tight text-center mt-0.5">{cap.message}</p>
            </div>
          </div>

          {/* Tip row */}
          <div className="mt-2.5 flex items-start gap-1.5 p-2 bg-white/[0.02] rounded-2xl border border-white/[0.05]">
            <Shield className="w-3 h-3 text-[#00F0FF] shrink-0 mt-0.5" />
            <p className="text-[9px] text-gray-400 font-plex leading-relaxed">
              <span className="text-[#00F0FF] font-semibold">How it works:</span> Each time you view a trade alert, we calculate
              the exact safe quantity based on these rules. If your capital can cover at least 1 share at 1% risk — you can trade it.
              Open any alert and tap <span className="text-white font-medium">"View Trade Info"</span> to see.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

const TIMEFRAME_MAP = { "1H": "Hourly", "1D": "Daily", "1W": "Weekly", "1M": "Monthly", "1Q": "Quarterly", "15 Min": "15m", "5 Min": "5m" };
const TIMEFRAMES = ["ALL", "1H", "1D", "1W", "1M", "1Q"];

// ── Compute live alert status ──────────────────────────────────────────────
function getAlertStatus(alert, cmp) {
  if (alert?.is_closed) {
    const reason = (alert.close_reason || "TARGET HIT").toUpperCase();
    const isTarget = reason.includes("TARGET") || reason.includes("PROFIT") || alert.target_hit;
    const isSL = reason.includes("SL") || reason.includes("STOP") || alert.sl_hit;
    const label = alert.close_reason || (isTarget ? "TARGET HIT" : isSL ? "SL HIT" : "CLOSED");
    const color = isTarget ? "#00FF88" : isSL ? "#FF3B30" : "#A855F7";
    const bg = isTarget ? "rgba(0,255,136,0.15)" : isSL ? "rgba(255,59,48,0.15)" : "rgba(168,85,247,0.15)";
    const icon = isTarget ? "🎯" : isSL ? "⛔" : "🔒";
    return { label, color, bg, icon, is_closed: true, close_price: alert.close_price, is_target_hit: isTarget, is_sl_hit: isSL };
  }

  if (!cmp || cmp <= 0) return null;
  const isDemand = alert.zone_type === "DEMAND";
  const entry = alert.entry;
  const target = alert.target;
  const sl = alert.stop_loss;

  // Target hit?
  if (isDemand && target && cmp >= target) return { label: "TARGET HIT", color: "#00FF88", bg: "rgba(0,255,136,0.12)", icon: "🎯", is_target_hit: true };
  if (!isDemand && target && cmp <= target) return { label: "TARGET HIT", color: "#00FF88", bg: "rgba(0,255,136,0.12)", icon: "🎯", is_target_hit: true };

  // SL hit?
  if (isDemand && sl && cmp <= sl) return { label: "SL HIT", color: "#FF3B30", bg: "rgba(255,59,48,0.12)", icon: "⛔", is_sl_hit: true };
  if (!isDemand && sl && cmp >= sl) return { label: "SL HIT", color: "#FF3B30", bg: "rgba(255,59,48,0.12)", icon: "⛔", is_sl_hit: true };

  // Near entry (within 0.5%)?
  if (entry && Math.abs((cmp - entry) / entry) <= 0.005) return { label: "NEAR ENTRY", color: "#FFCC00", bg: "rgba(255,204,0,0.10)", icon: "🎯" };

  // Moving toward target?
  if (isDemand && entry && cmp > entry) return { label: "ABOVE ENTRY", color: "#00F0FF", bg: "rgba(0,240,255,0.07)", icon: "↑" };
  if (!isDemand && entry && cmp < entry) return { label: "BELOW ENTRY", color: "#00F0FF", bg: "rgba(0,240,255,0.07)", icon: "↓" };

  return { label: "WATCHING", color: "#94A3B8", bg: "rgba(148,163,184,0.07)", icon: "👁" };
}

// ── Progress bar: entry → cmp → target ─────────────────────────────────────
function AlertProgressBar({ alert, cmp }) {
  const { theme } = useTheme();
  const isLight = theme === "light";
  if (!cmp || !alert.entry || !alert.target || !alert.stop_loss) return null;
  const isDemand = (alert.zone_type || "DEMAND").toUpperCase() === "DEMAND";

  // For DEMAND: SL (low) → Entry → Target (high)
  // For SUPPLY: Target (low) → Entry → SL (high)
  const low = isDemand ? alert.stop_loss : alert.target;
  const high = isDemand ? alert.target : alert.stop_loss;
  const range = high - low;
  if (range <= 0) return null;

  const clampedCmp = Math.max(low, Math.min(high, cmp));
  const entryPct = Math.max(0, Math.min(100, ((alert.entry - low) / range) * 100));
  const cmpPct = Math.max(0, Math.min(100, ((clampedCmp - low) / range) * 100));

  // Strict Target Progress calculation (measured strictly from Entry to Target):
  const targetDistance = isDemand ? (alert.target - alert.entry) : (alert.entry - alert.target);
  const targetGain = isDemand ? (cmp - alert.entry) : (alert.entry - cmp);
  const targetPctStrict = targetDistance > 0 ? (targetGain / targetDistance) * 100 : 0;

  // Strict SL Drawdown calculation (measured strictly from Entry to Stop Loss):
  const slDistance = isDemand ? (alert.entry - alert.stop_loss) : (alert.stop_loss - alert.entry);
  const slLoss = isDemand ? (alert.entry - cmp) : (cmp - alert.entry);
  const slPctStrict = slDistance > 0 ? (slLoss / slDistance) * 100 : 0;

  const status = getAlertStatus(alert, cmp);

  // Determine progress label & color strictly
  let progressLabel = "";
  let barColor = isLight ? "#0284C7" : "#00F0FF";

  if (status?.is_closed) {
    progressLabel = `${status.icon} ${status.label}${alert.close_price ? ` @ ₹${alert.close_price}` : ""}`;
    barColor = status.color;
  } else if (status?.label === "TARGET HIT" || targetPctStrict >= 100) {
    progressLabel = "🎯 Target Hit (100%)";
    barColor = isLight ? "#059669" : "#00FF88";
  } else if (status?.label === "SL HIT" || slPctStrict >= 100) {
    progressLabel = "⛔ SL Hit";
    barColor = isLight ? "#DC2626" : "#FF3B30";
  } else if (targetPctStrict > 0.5) {
    // In profit towards target
    progressLabel = `${Math.min(99.9, targetPctStrict).toFixed(0)}% towards Target`;
    barColor = isLight ? "#0284C7" : "#00F0FF";
  } else if (status?.label === "NEAR ENTRY" || Math.abs(targetGain) / alert.entry <= 0.005) {
    progressLabel = "⚡ At Entry Zone";
    barColor = isLight ? "#D97706" : "#FFCC00";
  } else {
    // In pullback below entry towards SL
    progressLabel = `⚡ Pullback (${Math.min(99.9, slPctStrict).toFixed(0)}% to SL)`;
    barColor = isLight ? "#D97706" : "#FF9500";
  }

  // Visual Bar:
  // Shows real live progress extending from Entry marker towards CMP
  const isProfit = cmpPct >= entryPct;
  const fillLeft = isProfit ? entryPct : cmpPct;
  const fillWidth = isProfit ? (cmpPct - entryPct) : (entryPct - cmpPct);
  const activeColor = isProfit ? barColor : (isLight ? "#DC2626" : "#FF3B30");

  return (
    <div className={`mb-3 p-2.5 rounded-2xl border transition-all ${
      isLight ? "bg-slate-50/70 border-slate-200/80" : "bg-white/[0.02] border-white/[0.04]"
    }`}>
      <div className="flex items-center justify-between mb-1.5">
        <span className={`text-[10px] font-jetbrains font-bold uppercase tracking-wider ${
          isLight ? "text-slate-600" : "text-gray-400 dark:text-[#94A3B8]"
        }`}>
          LIVE PROGRESS
        </span>
        <span className="text-[10px] font-jetbrains font-bold" style={{ color: barColor }}>
          {progressLabel}
        </span>
      </div>
      <div className={`relative h-2 rounded-full overflow-hidden ${
        isLight ? "bg-slate-200" : "bg-white/[0.08]"
      }`}>
        {/* Fill from Entry towards CMP */}
        <div
          className="absolute top-0 h-full rounded-full transition-all duration-500 shadow-[0_0_8px_currentColor]"
          style={{
            left: `${fillLeft}%`,
            width: `${Math.max(1, fillWidth)}%`,
            backgroundColor: activeColor,
            color: activeColor
          }}
        />
        {/* Entry line marker */}
        <div
          className={`absolute top-0 h-full w-1 z-10 shadow-sm ${
            isLight ? "bg-slate-700" : "bg-white"
          }`}
          style={{ left: `${entryPct}%` }}
          title={`Entry Level: ₹${formatPrice(alert.entry)}`}
        />
      </div>
      <div className="flex justify-between items-center mt-1.5 text-[9px] font-jetbrains">
        <span className={`font-bold ${isLight ? "text-rose-600" : "text-[#FF3B30]"}`}>
          SL: ₹{formatPrice(alert.stop_loss)}
        </span>
        <span className={`px-1.5 py-0.5 rounded font-semibold ${
          isLight ? "text-slate-700 bg-slate-200/80" : "text-gray-300 bg-white/10"
        }`}>
          Entry: ₹{formatPrice(alert.entry)}
        </span>
        <span className={`font-bold ${isLight ? "text-emerald-600" : "text-[#00FF88]"}`}>
          Target: ₹{formatPrice(alert.target)}
        </span>
      </div>
    </div>
  );
}

// ── Market Pulse Ticker ────────────────────────────────────────────────────
const MarketPulseTicker = ({ pulse }) => {
  const { theme } = useTheme();
  const isLight = theme === "light";
  if (!pulse || pulse.length === 0) {
    return (
      <div className="terminal-card rounded-2xl overflow-hidden h-[90px] flex items-center justify-center border border-gray-100 dark:border-white/[0.04]" data-testid="market-pulse-skeleton">
        <div className="animate-pulse flex space-x-4">
          <div className="h-4 w-32 bg-gray-200 dark:bg-white/10 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="terminal-card rounded-2xl overflow-hidden" data-testid="market-pulse">
      <div className="flex items-center gap-1.5 px-3 py-2 border-b border-gray-100 dark:border-white/[0.04]">
        <Activity className={`w-3.5 h-3.5 ${isLight ? "text-sky-700" : "text-[#00F0FF]"}`} />
        <span className={`text-[10px] font-jetbrains uppercase tracking-widest font-bold ${isLight ? "text-slate-700" : "text-gray-500 dark:text-[#94A3B8]"}`}>Market Pulse</span>
        {pulse.some(p => p.market_open) && (
          <span className="flex items-center gap-1 ml-auto">
            <span className={`w-1.5 h-1.5 rounded-full ${isLight ? "bg-emerald-600" : "bg-[#00FF88]"} animate-pulse`} />
            <span className={`text-[9px] font-jetbrains font-bold ${isLight ? "text-emerald-700" : "text-[#00FF88]"}`}>LIVE</span>
          </span>
        )}
      </div>
      <div className="flex overflow-x-auto gap-0 scrollbar-hide">
        {pulse.map((item, idx) => {
          const hasData = item.last_price > 0;
          const isUp = item.change >= 0;
          const color = !hasData ? (isLight ? "#64748B" : "#52525B") : isUp ? (isLight ? "#059669" : "#00FF88") : (isLight ? "#DC2626" : "#FF3B30");

          return (
            <div key={item.symbol || idx}
              className={`flex-shrink-0 px-4 py-2.5 ${idx < pulse.length - 1 ? "border-r border-gray-100 dark:border-white/[0.04]" : ""} hover:bg-gray-50 dark:bg-white/[0.02] transition-colors`}
              data-testid={`pulse-${item.symbol}`}>
              <div className="flex items-center gap-1.5 mb-0.5">
                <span className={`text-[10px] font-jetbrains font-bold ${isLight ? "text-slate-800" : "text-gray-500 dark:text-[#94A3B8]"}`}>{item.display || item.symbol}</span>
                {item.market_open ? (
                  <Wifi className={`w-2.5 h-2.5 ${isLight ? "text-emerald-600" : "text-[#00FF88]"}`} />
                ) : hasData ? (
                  <WifiOff className="w-2.5 h-2.5 text-gray-400 dark:text-[#52525B]" />
                ) : null}
              </div>
              {hasData ? (
                <>
                  <p className={`text-sm font-bold font-jetbrains ${isLight ? "text-gray-900" : "text-gray-900 dark:text-white"} leading-tight`}>
                    {formatPrice(item.last_price)}<span className="text-[9px] text-gray-400 dark:text-[#52525B] ml-0.5">{item.unit}</span>
                  </p>
                  <div className="flex items-center gap-1 mt-0.5">
                    {isUp ? <ArrowUpRight className="w-3 h-3" style={{ color }} /> : <ArrowDownRight className="w-3 h-3" style={{ color }} />}
                    <span className="text-[10px] font-jetbrains font-bold" style={{ color }}>
                      {item.change >= 0 ? "+" : ""}{formatPrice(item.change)} ({item.change_pct >= 0 ? "+" : ""}{item.change_pct}%)
                    </span>
                  </div>
                </>
              ) : (
                <p className="text-xs font-jetbrains text-gray-400 dark:text-[#52525B]">Market Closed</p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ── Alert Card ─────────────────────────────────────────────────────────────
const AlertCard = memo(({ alert, livePrice, onTradeClick, onDeleteClick, isAdmin, onEditClick, onCloseClick, onReopenClick }) => {
  const { theme } = useTheme();
  const isLight = theme === "light";
  const isDemand = alert.zone_type === "DEMAND";
  const accentColor = isDemand ? (isLight ? "#059669" : "#00FF88") : (isLight ? "#DC2626" : "#FF3B30");
  const bgAccent = isDemand ? (isLight ? "rgba(5, 150, 105, 0.08)" : "rgba(0,255,136,0.06)") : "rgba(255,59,48,0.06)";
  const Icon = isDemand ? ArrowUpRight : ArrowDownRight;
  const age = getTimeAgo(alert.created_at);
  const cmp = (livePrice?.last_price && livePrice.last_price > 0) ? livePrice.last_price : (alert.cmp || alert.entry);
  const entry = alert.entry;
  const distFromEntry = cmp && entry ? ((cmp - entry) / entry * 100).toFixed(2) : null;
  const status = getAlertStatus(alert, cmp);

  return (
    <div className="terminal-card rounded-2xl overflow-hidden group hover:border-gray-200 dark:border-white/10 transition-colors"
      style={{ borderLeft: `3px solid ${accentColor}` }} data-testid={`alert-${alert.alert_id}`}>

      {/* Header */}
      <div className="p-4 pb-3" style={{ background: bgAccent }}>
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Icon className="w-5 h-5" style={{ color: accentColor }} />
            <span className={`text-base font-bold font-outfit ${isLight ? "text-gray-900" : "text-gray-900 dark:text-white"}`} data-testid="alert-script">{alert.script_name || "N/A"}</span>
          </div>
          <div className="flex items-center gap-1.5 flex-wrap justify-end">
            {/* Live status badge */}
            {status && (
              <span
                className="text-[9px] font-bold font-jetbrains px-2 py-0.5 rounded-full"
                style={{ color: status.color, backgroundColor: status.bg }}
              >
                {status.icon} {status.label}
              </span>
            )}
            <Badge className={`border-0 text-[10px] font-jetbrains font-bold ${isDemand ? (isLight ? "bg-emerald-100 text-emerald-800" : "bg-[#00FF88]/15 text-[#00FF88]") : (isLight ? "bg-red-100 text-red-800" : "bg-[#FF3B30]/15 text-[#FF3B30]")
              }`} data-testid="alert-zone-type">
              {alert.zone_type}
            </Badge>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {alert.timeframe && (
            <Badge className={`border-0 text-[10px] font-jetbrains font-semibold ${isLight ? "bg-sky-100 text-sky-800" : "bg-[#00F0FF]/10 text-[#00F0FF]"}`}>
              <Clock className="w-3 h-3 mr-1" />{TIMEFRAME_MAP[alert.timeframe] || alert.timeframe}
            </Badge>
          )}
          {alert.setup_type && (
            <Badge className={`border-0 text-[10px] font-jetbrains font-semibold ${isLight ? "bg-amber-100 text-amber-800" : "bg-[#FFCC00]/10 text-[#FFCC00]"}`}>
              <Zap className="w-3 h-3 mr-1" />{alert.setup_type}
            </Badge>
          )}
          {alert.score && (
            <Badge className={`bg-gray-100 dark:bg-white/[0.06] ${isLight ? "text-gray-900 font-bold" : "text-gray-900 dark:text-white"} border-0 text-[10px] font-jetbrains`}>
              Score: {alert.score}
            </Badge>
          )}
        </div>
      </div>

      {/* Price Grid */}
      <div className="p-4 pt-3">
        {/* Live CMP Banner */}
        {cmp != null && cmp > 0 && (
          <div className="flex items-center justify-between p-2 mb-3 rounded bg-gray-50 dark:bg-white/[0.02] border border-gray-100 dark:border-white/[0.04]">
            <div className="flex items-center gap-2">
              <span className={`text-[9px] font-jetbrains uppercase font-bold ${isLight ? "text-slate-600" : "text-gray-400 dark:text-[#52525B]"}`}>CMP</span>
              <span className={`text-sm font-bold font-jetbrains ${isLight ? "text-gray-900" : "text-gray-900 dark:text-white"}`} data-testid="alert-cmp">
                {formatPrice(cmp)}
              </span>
              {(!livePrice?.is_fallback) && (
                <span className={`flex items-center gap-1 ${isLight ? "bg-emerald-100 text-emerald-800" : "bg-[#00FF88]/10 text-[#00FF88]"} px-1.5 py-0.5 rounded`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${isLight ? "bg-emerald-600" : "bg-[#00FF88]"} animate-pulse`} />
                  <span className="text-[8px] font-bold font-jetbrains">LIVE</span>
                </span>
              )}
            </div>
            {distFromEntry != null && (
              <span className={`text-[10px] font-jetbrains font-bold ${parseFloat(distFromEntry) >= 0 ? (isLight ? "text-emerald-700" : "text-[#00FF88]") : (isLight ? "text-red-700" : "text-[#FF3B30]")
                }`}>
                {parseFloat(distFromEntry) >= 0 ? "+" : ""}{distFromEntry}% from entry
              </span>
            )}
          </div>
        )}

        {/* Progress Bar */}
        <AlertProgressBar alert={alert} cmp={cmp} />

        <div className="grid grid-cols-2 gap-3 mb-3">
          {alert.entry != null && (
            <div
              onClick={() => isAdmin && onEditClick?.(alert)}
              className={isAdmin ? "cursor-pointer group/item rounded p-1 hover:bg-white/5 transition-colors" : ""}
              title={isAdmin ? "Click to edit setup levels" : ""}
            >
              <p className={`text-[9px] font-jetbrains uppercase font-bold ${isLight ? "text-slate-600" : "text-gray-400 dark:text-[#52525B]"}`}>Entry</p>
              <p className="text-sm font-bold font-jetbrains" style={{ color: accentColor }} data-testid="alert-entry">{formatPrice(alert.entry)}</p>
            </div>
          )}
          {alert.stop_loss != null && (
            <div
              onClick={() => isAdmin && onEditClick?.(alert)}
              className={isAdmin ? "cursor-pointer group/item rounded p-1 hover:bg-white/5 transition-colors" : ""}
              title={isAdmin ? "Click to edit setup levels" : ""}
            >
              <p className={`text-[9px] font-jetbrains uppercase font-bold ${isLight ? "text-slate-600" : "text-gray-400 dark:text-[#52525B]"}`}>Stop Loss</p>
              <p className={`text-sm font-bold font-jetbrains ${isLight ? "text-red-700" : "text-[#FF3B30]"}`} data-testid="alert-sl">{formatPrice(alert.stop_loss)}</p>
            </div>
          )}
          {alert.target != null && (
            <div
              onClick={() => isAdmin && onEditClick?.(alert)}
              className={isAdmin ? "cursor-pointer group/item rounded p-1 hover:bg-white/5 transition-colors" : ""}
              title={isAdmin ? "Click to edit setup levels" : ""}
            >
              <p className={`text-[9px] font-jetbrains uppercase font-bold ${isLight ? "text-slate-600" : "text-gray-400 dark:text-[#52525B]"}`}>Target</p>
              <p className={`text-sm font-bold font-jetbrains ${isLight ? "text-emerald-700" : "text-[#00FF88]"}`} data-testid="alert-target">{formatPrice(alert.target)}</p>
            </div>
          )}
          {alert.rr && (
            <div><p className={`text-[9px] font-jetbrains uppercase font-bold ${isLight ? "text-slate-600" : "text-gray-400 dark:text-[#52525B]"}`}>Risk:Reward</p>
              <p className={`text-sm font-bold font-jetbrains ${isLight ? "text-sky-700" : "text-[#00F0FF]"}`}>{alert.rr}</p>
            </div>
          )}
        </div>

        {/* Admin Remark */}
        {alert.admin_remark && (
          <div className={`mb-3 p-2.5 rounded border ${isLight ? "bg-amber-50/70 border-amber-200" : "bg-[#FFCC00]/[0.04] border-[#FFCC00]/10"}`} data-testid={`alert-remark-${alert.alert_id}`}>
            <div className="flex items-center gap-1 mb-0.5">
              <Shield className={`w-3 h-3 ${isLight ? "text-amber-700" : "text-[#FFCC00]"}`} />
              <span className={`text-[9px] font-jetbrains uppercase font-bold ${isLight ? "text-amber-800" : "text-[#FFCC00]"}`}>Admin Insight</span>
            </div>
            <p className={`text-[11px] font-plex leading-relaxed ${isLight ? "text-slate-800 font-medium" : "text-[#E2E8F0]"}`}>{alert.admin_remark}</p>
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between pt-2 border-t border-gray-100 dark:border-white/[0.04]">
          <div className="flex items-center gap-1.5 flex-wrap">
            <Button
              onClick={() => onTradeClick(alert, livePrice)}
              size="sm"
              className={`h-7 text-[10px] font-bold font-outfit uppercase px-2.5 rounded bg-gray-100 dark:bg-white/5 ${isLight ? "text-gray-900 border border-gray-300" : "text-gray-900 dark:text-white"} hover:bg-[#00F0FF] hover:text-black transition-colors`}
            >
              <HandCoins className="w-3 h-3 mr-1" />
              {isAdmin ? "Trade Setup" : "View Trade Info"}
            </Button>
            <Link to="/calculator">
              <Button
                size="sm"
                className={`h-7 text-[10px] font-bold font-mono px-2 rounded transition-colors ${isLight ? "bg-sky-50 text-sky-800 border border-sky-300 hover:bg-sky-100" : "bg-white/[0.04] text-[#00F0FF] border border-[#00F0FF]/20 hover:bg-[#00F0FF]/15"}`}
                title="Calculate lot sizing and risk for this trade"
              >
                <Calculator className="w-3 h-3 mr-1" />
                Calc
              </Button>
            </Link>

            {isAdmin && (
              <>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={(e) => {
                    e.stopPropagation();
                    onEditClick?.(alert);
                  }}
                  className="h-7 text-[10px] font-bold font-outfit uppercase px-2 rounded border-gray-300 dark:border-white/10 hover:border-[#00FF88] text-gray-700 dark:text-gray-200 hover:text-[#00FF88]"
                  title="Directly edit Entry, Stop Loss, Target, and Admin Remark"
                >
                  <SlidersHorizontal className="w-3 h-3 mr-1" />
                  Edit
                </Button>

                {alert.is_closed ? (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={(e) => {
                      e.stopPropagation();
                      onReopenClick?.(alert);
                    }}
                    className="h-7 text-[10px] font-bold font-outfit uppercase px-2 rounded border-purple-500/30 text-purple-400 hover:bg-purple-500/10"
                    title="Reopen trade to live status"
                  >
                    <RotateCcw className="w-3 h-3 mr-1" />
                    Reopen
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={(e) => {
                      e.stopPropagation();
                      onCloseClick?.(alert, livePrice);
                    }}
                    className="h-7 text-[10px] font-bold font-outfit uppercase px-2 rounded border-purple-500/30 text-purple-400 hover:bg-purple-500/15"
                    title="Close trade (Target Hit, SL Hit, Manual Exit)"
                  >
                    <Target className="w-3 h-3 mr-1" />
                    Close
                  </Button>
                )}
              </>
            )}
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            {isAdmin && (
              <Button
                size="sm"
                variant="ghost"
                onClick={(e) => {
                  e.stopPropagation();
                  onDeleteClick?.(alert);
                }}
                className="h-7 w-7 p-0 text-gray-400 hover:text-[#FF3B30] hover:bg-[#FF3B30]/10 rounded transition-colors"
                title="Delete Alert (Removes from all users immediately)"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            )}
            <span className="text-[10px] font-jetbrains text-gray-400 dark:text-[#52525B]">{age}</span>
          </div>
        </div>
      </div>
    </div>
  );
});

// ── Main Page ──────────────────────────────────────────────────────────────
const DrishtiAlertsPage = () => {
  const { theme } = useTheme();
  const { user, loading: authLoading, checkAuth } = useAuth();
  const isLight = theme === "light";
  const isAdmin = user?.role === "admin" || user?.role === "superadmin";

  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(isAdmin ? true : false);
  const [statsData, setStatsData] = useState(null);
  const [pricingModalOpen, setPricingModalOpen] = useState(false);
  const [viewTab, setViewTab] = useState("ACTIVE"); // 'ACTIVE' | 'TARGET_HIT' | 'SL_HIT' | 'CLOSED'
  const [filterTF, setFilterTF] = useState("ALL");
  const [filterType, setFilterType] = useState("ALL");
  const [filterStatus, setFilterStatus] = useState("ALL"); // ALL, NEAR_ENTRY
  const [search, setSearch] = useState("");
  const [pulse, setPulse] = useState([]);
  const [livePrices, setLivePrices] = useState({});
  const [lastRefresh, setLastRefresh] = useState(null);

  // Real-time access sync: whenever admin approves/revokes and useAuth updates, immediately unlock/lock terminal
  useEffect(() => {
    if (isAdmin) {
      setHasAccess(true);
    } else if (user?.has_drishti_access === true) {
      setHasAccess(true);
    } else if (user?.has_drishti_access === false) {
      setHasAccess(false);
    }
  }, [isAdmin, user?.has_drishti_access]);

  const userHasAccess = Boolean(isAdmin || hasAccess || user?.has_drishti_access === true);

  const [connectModalOpen, setConnectModalOpen] = useState(false);
  const [tradeModalOpen, setTradeModalOpen] = useState(false);
  const [infoModalOpen, setInfoModalOpen] = useState(false);
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [selectedLivePrice, setSelectedLivePrice] = useState(null);
  const [dhanConnected, setDhanConnected] = useState(false);
  const [capacityData, setCapacityData] = useState(null); // user trading capacity

  // Admin Direct Edit & Close Modals
  const [editModalAlert, setEditModalAlert] = useState(null);
  const [closeModalAlert, setCloseModalAlert] = useState(null);

  // ── Alert Notifications: audio blip + browser push on new alerts ────
  const { permission: notifPermission, requestPermission } = useAlertNotifications(alerts.length);

  useEffect(() => {
    axios.get(`${API}/trading/dhan-status`, { withCredentials: true })
      .then(res => setDhanConnected(res.data?.connected || false))
      .catch(() => setDhanConnected(false));
  }, []);

  // Fetch trading capacity for regular users so the AlertInfoModal gets it pre-loaded
  const fetchCapacity = useCallback(async () => {
    if (isAdmin) return; // admin doesn't need this
    try {
      const res = await axios.get(`${API}/trading/trading-capacity`, { withCredentials: true });
      setCapacityData(res.data);
    } catch { /* silent */ }
  }, [isAdmin]);

  useEffect(() => { fetchCapacity(); }, [fetchCapacity]);

  const handleTradeClick = (alert, livePrice) => {
    setSelectedAlert(alert);
    setSelectedLivePrice(livePrice);
    if (isAdmin) {
      // Admin: full 1-click execution modal
      setTradeModalOpen(true);
    } else {
      // Regular users: rich info panel for manual trading
      setInfoModalOpen(true);
    }
  };

  const handleDeleteAlert = useCallback(async (alert) => {
    if (!window.confirm(`Permanently delete alert for ${alert.script_name}? This will remove it for all users immediately.`)) return;
    try {
      await axios.delete(`${API}/drishti/alerts/${alert.alert_id || alert.id}`, { withCredentials: true });
      toast.success(`Alert for ${alert.script_name} permanently deleted`);
      setAlerts(prev => prev.filter(a => (a.alert_id || a.id) !== (alert.alert_id || alert.id)));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete alert");
    }
  }, []);

  const fetchAlerts = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/drishti/alerts?limit=300`, { withCredentials: true });
      if (res.data) {
        if (typeof res.data.has_access === "boolean") {
          setHasAccess(res.data.has_access);
          if (res.data.has_access === false && user?.has_drishti_access) {
            checkAuth?.();
          }
        }
        if (res.data.stats) {
          setStatsData(res.data.stats);
        }
      }
      setAlerts(asArray(res.data, "alerts"));
    } catch (err) { console.error("Error fetching alerts:", err); }
    finally { setLoading(false); }
  }, [user?.has_drishti_access, checkAuth]);

  const handleReopenAlert = useCallback(async (alert) => {
    const alertId = alert.alert_id || alert.id;
    try {
      await axios.put(`${API}/drishti/alerts/${alertId}/reopen`, {}, { withCredentials: true });
      toast.success(`Trade for ${alert.script_name} reopened and restored to live state!`);
      setAlerts(prev => prev.map(a => (a.alert_id || a.id) === alertId ? { ...a, is_closed: false, close_reason: null, close_price: null } : a));
      fetchAlerts();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to reopen trade");
    }
  }, [fetchAlerts]);

  const fetchMarketPulse = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/market/pulse`);
      setPulse(asArray(res.data, "pulse"));
      setLastRefresh(new Date());
    } catch (err) { console.error("Error fetching market pulse:", err); }
  }, []);

  const fetchLivePrices = useCallback(async (alertsList) => {
    // Only check open alerts to keep requests minimal
    const openAlerts = alertsList.filter(a => !a.is_closed);
    const symbols = [...new Set(openAlerts.map(a => a.script_name).filter(Boolean))];
    if (symbols.length === 0) return;
    
    const chunkSize = 20;
    const autoCloseMap = {};
    const batchPrices = {};
    const closedAlertUpdates = [];

    for (let i = 0; i < symbols.length; i += chunkSize) {
      const chunk = symbols.slice(i, i + chunkSize);
      try {
        const res = await axios.get(`${API}/market/ltp?symbols=${encodeURIComponent(chunk.join(","))}`);
        if (res.data?.data) {
          const newPrices = res.data.data;
          Object.assign(batchPrices, newPrices);

          // Real-time auto-close evaluation for open alerts
          alertsRef.current.forEach(alert => {
            if (alert.is_closed) return;
            const sym = alert.script_name;
            const priceObj = newPrices[sym];
            if (!priceObj || !priceObj.last_price || priceObj.last_price <= 0) return;
            const cmp = Number(priceObj.last_price);
            const target = Number(alert.target || 0);
            const sl = Number(alert.stop_loss || 0);
            const isDemand = alert.zone_type === "DEMAND";

            let hitTarget = false;
            let hitSL = false;

            if (isDemand) {
              if (target > 0 && cmp >= target) hitTarget = true;
              else if (sl > 0 && cmp <= sl) hitSL = true;
            } else { // SUPPLY
              if (target > 0 && cmp <= target) hitTarget = true;
              else if (sl > 0 && cmp >= sl) hitSL = true;
            }

            if (hitTarget || hitSL) {
              const reason = hitTarget ? "TARGET HIT" : "SL HIT";
              autoCloseMap[sym] = cmp;
              closedAlertUpdates.push({
                id: alert.alert_id || alert.id,
                reason,
                hitTarget,
                hitSL,
                cmp
              });

              if (hitTarget) {
                toast.success(`🎯 Target Hit: ${alert.script_name} reached target of ₹${target}! Closed in profit.`);
              } else {
                toast.error(`⛔ Stop Loss Hit: ${alert.script_name} reached SL of ₹${sl}. Closed to protect capital.`);
              }
            }
          });
        }
      } catch (err) { console.error("Error fetching live prices chunk:", err); }
    }

    // Single atomic state update for live prices to avoid continuous DOM stutter
    if (Object.keys(batchPrices).length > 0) {
      setLivePrices(prev => ({ ...prev, ...batchPrices }));
    }

    // Single atomic state update for closed alerts
    if (closedAlertUpdates.length > 0) {
      const updateMap = new Map();
      closedAlertUpdates.forEach(u => updateMap.set(u.id, u));
      setAlerts(prev => prev.map(a => {
        const match = updateMap.get(a.alert_id || a.id);
        if (match) {
          return {
            ...a,
            is_closed: true,
            close_reason: match.reason,
            target_hit: match.hitTarget,
            sl_hit: match.hitSL,
            close_price: match.cmp,
            closed_at: new Date().toISOString()
          };
        }
        return a;
      }));
    }

    // Persist any auto-closures to backend without causing fetchAlerts recursion
    if (Object.keys(autoCloseMap).length > 0) {
      axios.post(`${API}/drishti/alerts/auto-close-check`, { prices: autoCloseMap }, { withCredentials: true })
        .catch(err => console.warn("Auto-close sync error:", err));
    }
  }, []);

  // ── Refs for stable interval handles ──────────────────────────────────
  const alertsRef = useRef([]);
  const priceIntervalRef = useRef(null);
  const alertIntervalRef = useRef(null);
  const isFetchingPricesRef = useRef(false);

  // Keep alertsRef in sync so interval always sees latest alerts
  useEffect(() => { alertsRef.current = alerts; }, [alerts]);

  // ── Initial load ─────────────────────────────────────────────────────
  useEffect(() => {
    fetchAlerts();
    fetchMarketPulse();
  }, [fetchAlerts, fetchMarketPulse]);

  // Fetch prices once when alerts first load
  const initialFetchDone = useRef(false);
  useEffect(() => {
    if (alerts.length > 0 && !initialFetchDone.current) {
      initialFetchDone.current = true;
      fetchLivePrices(alerts);
    }
  }, [alerts, fetchLivePrices]);

  // ── Price polling: runs every 10s only when tab visible and user has access ────────────
  useEffect(() => {
    if (!userHasAccess) return;

    const startPricePolling = () => {
      if (priceIntervalRef.current) return;
      priceIntervalRef.current = setInterval(async () => {
        if (alertsRef.current.length > 0 && !isFetchingPricesRef.current) {
          isFetchingPricesRef.current = true;
          try {
            await fetchLivePrices(alertsRef.current);
          } finally {
            isFetchingPricesRef.current = false;
          }
        }
      }, 10000); // 10s smooth interval
    };

    const stopPricePolling = () => {
      if (priceIntervalRef.current) {
        clearInterval(priceIntervalRef.current);
        priceIntervalRef.current = null;
      }
    };

    const onVisibility = () => {
      if (document.hidden) {
        stopPricePolling();
      } else {
        if (alertsRef.current.length > 0) fetchLivePrices(alertsRef.current);
        startPricePolling();
      }
    };

    startPricePolling();
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      stopPricePolling();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [userHasAccess, fetchLivePrices]);

  // ── Regular polling: alerts + pulse every 30s only for authorized users ─────────────
  useEffect(() => {
    if (!userHasAccess) return;
    alertIntervalRef.current = setInterval(() => {
      fetchAlerts();
      fetchMarketPulse();
    }, 30000);
    return () => clearInterval(alertIntervalRef.current);
  }, [userHasAccess, fetchAlerts, fetchMarketPulse]);


  // Helper to determine if an alert is completed / closed
  const isAlertCompleted = useCallback((a, cmp) => {
    if (a.is_closed || a.target_hit || a.sl_hit) return true;
    const s = getAlertStatus(a, cmp);
    return Boolean(s?.is_closed || s?.is_target_hit || s?.is_sl_hit);
  }, []);

  const filtered = alerts.filter(a => {
    const cmp = livePrices[a.script_name]?.last_price || a.cmp;
    const s = getAlertStatus(a, cmp);
    const completed = isAlertCompleted(a, cmp);
    const isTargetHit = (a.is_closed && (a.target_hit || a.close_reason?.toUpperCase()?.includes("TARGET") || a.close_reason?.toUpperCase()?.includes("PROFIT"))) || s?.is_target_hit || s?.label === "TARGET HIT" || s?.label?.toUpperCase()?.includes("PROFIT");
    const isSLHit = (a.is_closed && (a.sl_hit || a.close_reason?.toUpperCase()?.includes("SL") || a.close_reason?.toUpperCase()?.includes("STOP"))) || s?.is_sl_hit || s?.label === "SL HIT";

    // ── Primary Tab Separation ──
    if (viewTab === "ACTIVE") {
      // STRICTLY EXCLUDE completed / closed trades from Active view!
      if (completed) return false;
      if (filterStatus === "NEAR_ENTRY" && s?.label !== "NEAR ENTRY") return false;
    } else if (viewTab === "TARGET_HIT") {
      if (!isTargetHit) return false;
    } else if (viewTab === "SL_HIT") {
      if (!isSLHit) return false;
    } else if (viewTab === "CLOSED") {
      if (!completed && !a.is_closed) return false;
    }

    if (filterTF !== "ALL" && a.timeframe !== filterTF) return false;
    if (filterType !== "ALL" && a.zone_type !== filterType) return false;
    if (search && !a.script_name?.toLowerCase().includes(search.toLowerCase())) return false;

    return true;
  });

  // Calculate live counts
  const activeAlerts = alerts.filter(a => !isAlertCompleted(a, livePrices[a.script_name]?.last_price || a.cmp));
  const activeCount = activeAlerts.length;
  const activeDemandCount = activeAlerts.filter(a => a.zone_type === "DEMAND").length;
  const activeSupplyCount = activeAlerts.filter(a => a.zone_type === "SUPPLY").length;
  const targetHitCount = alerts.filter(a => {
    const cmp = livePrices[a.script_name]?.last_price || a.cmp;
    const s = getAlertStatus(a, cmp);
    return (a.is_closed && (a.target_hit || a.close_reason?.toUpperCase()?.includes("TARGET") || a.close_reason?.toUpperCase()?.includes("PROFIT"))) || s?.is_target_hit || s?.label === "TARGET HIT" || s?.label?.toUpperCase()?.includes("PROFIT");
  }).length;
  const slHitCount = alerts.filter(a => {
    const cmp = livePrices[a.script_name]?.last_price || a.cmp;
    const s = getAlertStatus(a, cmp);
    return (a.is_closed && (a.sl_hit || a.close_reason?.toUpperCase()?.includes("SL") || a.close_reason?.toUpperCase()?.includes("STOP"))) || s?.is_sl_hit || s?.label === "SL HIT";
  }).length;
  const closedCount = alerts.filter(a => a.is_closed).length;

  if (authLoading || loading) {
    return (
      <DashboardLayout title="Drishti AI">
        <div className="flex flex-col items-center justify-center h-80 space-y-4">
          <div className="w-12 h-12 border-4 border-[#00FF88] border-t-transparent rounded-full animate-spin shadow-[0_0_24px_rgba(0,255,136,0.25)]" />
          <p className="text-xs font-jetbrains text-gray-400 dark:text-[#94A3B8] tracking-widest uppercase">
            Loading Drishti AI Market Terminal...
          </p>
        </div>
      </DashboardLayout>
    );
  }

  // Full-page gating: Only open paywall if not admin and does not have access
  if (!userHasAccess) {
    return (
      <DashboardLayout title="Drishti AI">
        <DrishtiPaywallGate
          featureName="Drishti AI Live Signals"
          onAccessGranted={async () => {
            setHasAccess(true);
            try {
              if (checkAuth) await checkAuth();
            } catch (_) {}
            fetchAlerts();
          }}
        />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Drishti AI">
      <div className="space-y-4" data-testid="drishti-alerts-page">
        {/* Market Pulse Ticker */}
        <MarketPulseTicker pulse={pulse} />

        {/* Trading Capacity Widget — users only (admin sees DhanPortfolioWidget) */}
        {!isAdmin && (
          <TradingCapacityWidget onConnectClick={() => setConnectModalOpen(true)} />
        )}

        {/* Connect Dhan Modal for users too (triggered from TradingCapacityWidget) */}
        {!isAdmin && (
          <ConnectDhanModal
            open={connectModalOpen}
            onOpenChange={setConnectModalOpen}
            onConnected={() => { setConnectModalOpen(false); fetchCapacity(); }}
          />
        )}

        {/* Dhan Portfolio — admin only */}
        {isAdmin && <DhanPortfolioWidget />}

        {/* Header Overview */}
        <div className="terminal-card rounded-2xl p-5 overflow-hidden relative" data-testid="drishti-unified-overview">
          <div className="absolute inset-0 grid-terminal opacity-30 pointer-events-none" />
          <div className="absolute -right-16 -top-16 w-48 h-48 rounded-full bg-[#00FF88]/10 blur-3xl pointer-events-none" />
          <div className="relative flex flex-col lg:flex-row gap-5 lg:items-center justify-between">
            <div className="flex gap-4">
              <div className="w-12 h-12 rounded-xl bg-[#00FF88]/10 border border-[#00FF88]/20 flex items-center justify-center shrink-0 shadow-[0_0_22px_rgba(0,255,136,0.16)]">
                <Zap className="w-6 h-6 text-[#00FF88]" />
              </div>
              <div>
                <p className={`text-[10px] font-jetbrains tracking-[0.2em] font-bold uppercase ${isLight ? "text-sky-700" : "text-[#00F0FF]"}`}>Drishti AI Live</p>
                <h1 className={`text-xl md:text-2xl font-bold ${isLight ? "!text-slate-900" : "text-white"} font-outfit mt-1`}>Live alerts with real-time price tracking</h1>
                <p className={`text-sm font-plex mt-1 max-w-2xl ${isLight ? "text-slate-700 font-medium" : "text-gray-300 dark:text-[#CBD5E1]"}`}>
                  {isAdmin
                    ? "Admin mode — full 1-click trade execution enabled. Each alert shows live CMP, target status, SL status and entry-to-target progress."
                    : "Get complete trade setups — entry, SL, target, R:R and risk sizing. Trade manually on your broker app using these details."}
                </p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 shrink-0">
              {/* Admin: show Dhan connect status */}
              {isAdmin && (
                dhanConnected ? (
                  <div className={`flex items-center gap-1.5 px-3 h-9 rounded-xl border ${isLight ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "bg-[#00FF88]/[0.06] border-[#00FF88]/30 text-[#00FF88]"}`}>
                    <CheckCircle2 className={`w-3.5 h-3.5 ${isLight ? "text-emerald-600" : "text-[#00FF88]"}`} />
                    <span className="text-xs font-jetbrains font-bold">Dhan Connected</span>
                  </div>
                ) : (
                  <Button onClick={() => setConnectModalOpen(true)} variant="outline" className={`rounded-xl border ${isLight ? "border-slate-300 text-slate-800 hover:bg-slate-100" : "border-[#1E293B] text-white hover:bg-white/5"}`} data-testid="connect-broker-btn">
                    <HandCoins className={`w-3.5 h-3.5 mr-2 ${isLight ? "text-amber-600" : "text-[#FFCC00]"}`} /> Connect Dhan
                  </Button>
                )
              )}
              {/* All users: Notification permission bell */}
              <Button
                variant="outline"
                onClick={requestPermission}
                title={notifPermission === "granted" ? "Push notifications ON" : "Enable push notifications for new alerts"}
                className={`h-9 w-9 p-0 rounded-xl border ${
                  notifPermission === "granted"
                    ? isLight ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-[#00FF88]/30 bg-[#00FF88]/[0.06] text-[#00FF88]"
                    : isLight ? "border-amber-200 bg-amber-50 text-amber-700" : "border-[#FFCC00]/30 bg-[#FFCC00]/[0.05] text-[#FFCC00]"
                }`}
                data-testid="notif-permission-btn"
              >
                {notifPermission === "granted" ? <Bell className="w-4 h-4" /> : <BellOff className="w-4 h-4" />}
              </Button>
              {/* All users: Risk Calculator Button */}
              <Link to="/calculator">
                <Button
                  variant="outline"
                  className={`h-9 px-3.5 text-xs font-bold font-outfit rounded-xl border transition-all shadow-xs ${
                    isLight 
                      ? "border-sky-300 bg-sky-50 text-sky-700 hover:bg-sky-100" 
                      : "border-[#00F0FF]/30 bg-[#00F0FF]/[0.06] text-[#00F0FF] hover:bg-[#00F0FF]/15 shadow-[0_0_12px_rgba(0,240,255,0.15)]"
                  }`}
                  data-testid="drishti-calc-btn"
                >
                  <Calculator className="w-3.5 h-3.5 mr-1.5" /> Risk Calculator
                </Button>
              </Link>
              {/* Pro status or Unlock Pro CTA button */}
              {!isAdmin && !hasAccess && (
                <Button
                  onClick={() => setPricingModalOpen(true)}
                  className="bg-gradient-to-r from-[#00FF88] to-[#00F0FF] !text-black font-black font-outfit rounded-xl h-9 text-xs shadow-[0_0_16px_rgba(0,255,136,0.3)] hover:brightness-110"
                  data-testid="unlock-pro-header-btn"
                >
                  <Zap className="w-3.5 h-3.5 mr-1.5" /> Unlock Pro
                </Button>
              )}
              {!isAdmin && hasAccess && (
                <div className={`flex items-center gap-1.5 px-3 h-9 rounded-xl border ${
                  isLight ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "bg-[#00FF88]/[0.08] border-[#00FF88]/30 text-[#00FF88]"
                }`}>
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  <span className="text-xs font-jetbrains font-bold">Pro Active</span>
                </div>
              )}
              {/* All users: Telegram alerts */}
              <Button onClick={() => window.open("https://t.me/+QOePMys7V481NmZl", "_blank")} className="bg-[#0088cc] !text-white hover:brightness-110 rounded-xl h-9 text-xs font-bold font-outfit shadow-xs" data-testid="telegram-join-btn">
                <Send className="w-3.5 h-3.5 mr-1.5" /> Telegram <ExternalLink className="w-3.5 h-3.5 ml-1.5" />
              </Button>

            </div>
          </div>
        </div>

        {/* Stats Summary Cards (Clickable shortcuts) */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div 
            onClick={() => { setViewTab("ACTIVE"); setFilterType("ALL"); }}
            className={`terminal-card rounded-2xl p-4 cursor-pointer transition-all ${
              viewTab === "ACTIVE" && filterType === "ALL" 
                ? isLight ? "ring-2 ring-emerald-500 bg-emerald-50/50" : "ring-2 ring-[#00FF88] bg-[#00FF88]/[0.04]"
                : "hover:border-white/20"
            }`}
          >
            <div className="flex items-center gap-2 mb-1">
              <Activity className={`w-4 h-4 ${isLight ? "text-sky-600" : "text-[#00F0FF]"}`} />
              <span className={`text-[10px] font-jetbrains uppercase tracking-wider font-semibold ${isLight ? "text-slate-600" : "text-gray-400 dark:text-[#94A3B8]"}`}>Active Alerts</span>
            </div>
            <p className={`text-2xl font-bold font-jetbrains ${isLight ? "text-slate-900" : "text-white"}`} data-testid="total-alerts">{activeCount || statsData?.active_count || 0}</p>
            <p className="text-[10px] text-zinc-500 font-mono mt-0.5">Live open positions</p>
          </div>
          <div 
            onClick={() => { setViewTab("ACTIVE"); setFilterType("DEMAND"); }}
            className={`terminal-card rounded-2xl p-4 cursor-pointer transition-all ${
              viewTab === "ACTIVE" && filterType === "DEMAND" 
                ? isLight ? "ring-2 ring-emerald-500 bg-emerald-50/50" : "ring-2 ring-[#00FF88] bg-[#00FF88]/[0.04]"
                : "hover:border-white/20"
            }`}
          >
            <div className="flex items-center gap-2 mb-1">
              <TrendingUp className={`w-4 h-4 ${isLight ? "text-emerald-600" : "text-[#00FF88]"}`} />
              <span className={`text-[10px] font-jetbrains uppercase tracking-wider font-semibold ${isLight ? "text-slate-600" : "text-gray-400 dark:text-[#94A3B8]"}`}>Demand (Live)</span>
            </div>
            <p className={`text-2xl font-bold font-jetbrains ${isLight ? "text-emerald-600" : "text-[#00FF88]"}`}>{activeDemandCount || statsData?.demand_count || 0}</p>
            <p className="text-[10px] text-zinc-500 font-mono mt-0.5">Active buy setups</p>
          </div>
          <div 
            onClick={() => { setViewTab("ACTIVE"); setFilterType("SUPPLY"); }}
            className={`terminal-card rounded-2xl p-4 cursor-pointer transition-all ${
              viewTab === "ACTIVE" && filterType === "SUPPLY" 
                ? isLight ? "ring-2 ring-rose-500 bg-rose-50/50" : "ring-2 ring-[#FF3B30] bg-[#FF3B30]/[0.04]"
                : "hover:border-white/20"
            }`}
          >
            <div className="flex items-center gap-2 mb-1">
              <TrendingDown className={`w-4 h-4 ${isLight ? "text-rose-600" : "text-[#FF3B30]"}`} />
              <span className={`text-[10px] font-jetbrains uppercase tracking-wider font-semibold ${isLight ? "text-slate-600" : "text-gray-400 dark:text-[#94A3B8]"}`}>Supply (Live)</span>
            </div>
            <p className={`text-2xl font-bold font-jetbrains ${isLight ? "text-rose-600" : "text-[#FF3B30]"}`}>{activeSupplyCount || statsData?.supply_count || 0}</p>
            <p className="text-[10px] text-zinc-500 font-mono mt-0.5">Active sell setups</p>
          </div>
          <div 
            onClick={() => { setViewTab("TARGET_HIT"); setFilterType("ALL"); }}
            className={`terminal-card rounded-2xl p-4 cursor-pointer transition-all ${
              viewTab === "TARGET_HIT" 
                ? isLight ? "ring-2 ring-amber-500 bg-amber-50/50" : "ring-2 ring-[#FFCC00] bg-[#FFCC00]/[0.04]"
                : "hover:border-white/20"
            }`}
          >
            <div className="flex items-center gap-2 mb-1">
              <Trophy className={`w-4 h-4 ${isLight ? "text-amber-600" : "text-[#FFCC00]"}`} />
              <span className={`text-[10px] font-jetbrains uppercase tracking-wider font-semibold ${isLight ? "text-slate-600" : "text-gray-400 dark:text-[#94A3B8]"}`}>Target Hit</span>
            </div>
            <p className={`text-2xl font-bold font-jetbrains ${isLight ? "text-amber-600" : "text-[#FFCC00]"}`} data-testid="target-hit-count">{targetHitCount || statsData?.target_hit_count || 0}</p>
            <p className={`text-[9px] font-jetbrains mt-0.5 ${isLight ? "text-slate-500 font-medium" : "text-gray-400 dark:text-[#52525B]"}`}>Closed in profit</p>
          </div>
        </div>

        {/* Filters & Dedicated Active/Closed Segment Control */}
        <div className="terminal-card rounded-2xl p-4 space-y-3.5">
          {/* Top Row: Primary Tab Switcher (Active vs Target Hit vs SL Hit vs Closed) */}
          <div className="flex flex-col lg:flex-row items-stretch lg:items-center justify-between gap-3">
            <div className={`flex items-center gap-1.5 p-1 rounded-xl border ${isLight ? "bg-slate-100/90 border-slate-200" : "bg-black/50 border-white/[0.08]"} overflow-x-auto custom-scrollbar`}>
              <button
                type="button"
                onClick={() => { setViewTab("ACTIVE"); setFilterStatus("ALL"); }}
                className={`flex items-center gap-2 px-3.5 py-1.5 rounded-lg text-xs font-jetbrains font-bold transition-all whitespace-nowrap ${
                  viewTab === "ACTIVE"
                    ? isLight ? "bg-emerald-600 !text-white shadow-xs" : "bg-[#00FF88] !text-black shadow-[0_0_14px_rgba(0,255,136,0.35)]"
                    : isLight ? "text-slate-600 hover:text-slate-900 hover:bg-white" : "text-[#94A3B8] hover:text-white hover:bg-white/5"
                }`}
              >
                <span className={`w-2 h-2 rounded-full ${viewTab === "ACTIVE" ? (isLight ? "bg-white" : "bg-black") : "bg-[#00FF88]"} animate-pulse`} />
                <span>Live Active Trades</span>
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-mono font-bold transition-colors ${
                  viewTab === "ACTIVE" 
                    ? isLight ? "bg-white/30 !text-white" : "bg-black/25 !text-black" 
                    : isLight ? "bg-slate-200/90 text-slate-800 border border-slate-300/50" : "bg-white/10 text-[#CBD5E1]"
                }`}>
                  {activeCount}
                </span>
              </button>

              <button
                type="button"
                onClick={() => { setViewTab("TARGET_HIT"); setFilterStatus("ALL"); }}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-jetbrains font-bold transition-all whitespace-nowrap ${
                  viewTab === "TARGET_HIT"
                    ? isLight ? "bg-amber-600 !text-white shadow-xs" : "bg-[#FFCC00] !text-black shadow-[0_0_14px_rgba(255,204,0,0.35)]"
                    : isLight ? "text-slate-600 hover:text-slate-900 hover:bg-white" : "text-[#94A3B8] hover:text-white hover:bg-white/5"
                }`}
              >
                <Trophy className="w-3.5 h-3.5" />
                <span>Target Hit</span>
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-mono font-bold transition-colors ${
                  viewTab === "TARGET_HIT" 
                    ? isLight ? "bg-white/30 !text-white" : "bg-black/25 !text-black" 
                    : isLight ? "bg-slate-200/90 text-slate-800 border border-slate-300/50" : "bg-white/10 text-[#CBD5E1]"
                }`}>
                  {targetHitCount}
                </span>
              </button>

              <button
                type="button"
                onClick={() => { setViewTab("SL_HIT"); setFilterStatus("ALL"); }}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-jetbrains font-bold transition-all whitespace-nowrap ${
                  viewTab === "SL_HIT"
                    ? isLight ? "bg-rose-600 !text-white shadow-xs" : "bg-[#FF3B30] !text-white shadow-[0_0_14px_rgba(255,59,48,0.35)]"
                    : isLight ? "text-slate-600 hover:text-slate-900 hover:bg-white" : "text-[#94A3B8] hover:text-white hover:bg-white/5"
                }`}
              >
                <AlertCircle className="w-3.5 h-3.5" />
                <span>SL Hit</span>
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-mono font-bold transition-colors ${
                  viewTab === "SL_HIT" 
                    ? isLight ? "bg-white/30 !text-white" : "bg-white/20 !text-white" 
                    : isLight ? "bg-slate-200/90 text-slate-800 border border-slate-300/50" : "bg-white/10 text-[#CBD5E1]"
                }`}>
                  {slHitCount}
                </span>
              </button>

              <button
                type="button"
                onClick={() => { setViewTab("CLOSED"); setFilterStatus("ALL"); }}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-jetbrains font-bold transition-all whitespace-nowrap ${
                  viewTab === "CLOSED"
                    ? isLight ? "bg-purple-600 !text-white shadow-xs" : "bg-purple-600 !text-white shadow-[0_0_14px_rgba(168,85,247,0.35)]"
                    : isLight ? "text-slate-600 hover:text-slate-900 hover:bg-white" : "text-[#94A3B8] hover:text-white hover:bg-white/5"
                }`}
              >
                <Lock className="w-3.5 h-3.5" />
                <span>All Closed</span>
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-mono font-bold transition-colors ${
                  viewTab === "CLOSED" 
                    ? isLight ? "bg-white/30 !text-white" : "bg-white/20 !text-white" 
                    : isLight ? "bg-slate-200/90 text-slate-800 border border-slate-300/50" : "bg-white/10 text-[#CBD5E1]"
                }`}>
                  {closedCount}
                </span>
              </button>
            </div>

            {/* Search Input */}
            <div className="relative flex-1 max-w-sm">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-[#52525B]" />
              <Input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search symbol (RELIANCE, NIFTY)..."
                className={`pl-9 h-9 w-full ${isLight ? "bg-white border-slate-300 text-slate-900 placeholder:text-slate-400 focus:border-sky-500 shadow-xs" : "bg-gray-50 dark:bg-white/[0.02] border-gray-200 dark:border-white/[0.06] text-white placeholder:text-[#52525B]"} font-plex text-xs`}
                data-testid="search-input"
              />
            </div>
          </div>

          {/* Secondary Filter Clusters: TF & Zone */}
          <div className="flex flex-wrap items-center justify-between gap-2.5 pt-2 border-t border-slate-100 dark:border-white/[0.04]">
            <div className="flex flex-wrap items-center gap-2">
              {/* Timeframe Cluster */}
              <div className={`flex items-center gap-1 p-1 rounded-xl border ${isLight ? "bg-slate-100/80 border-slate-200/80" : "bg-white/[0.02] border-white/[0.06]"} overflow-x-auto custom-scrollbar`}>
                <span className={`text-[10px] font-jetbrains uppercase font-bold px-2 hidden sm:inline-block ${isLight ? "text-slate-500" : "text-gray-400"}`}>TF:</span>
                {TIMEFRAMES.map(tf => {
                  const active = filterTF === tf;
                  return (
                    <button
                      key={tf}
                      onClick={() => setFilterTF(tf)}
                      className={`text-xs font-jetbrains h-7 px-2 rounded-lg transition-all flex items-center justify-center font-bold ${
                        active
                          ? isLight
                            ? "bg-sky-600 !text-white shadow-xs"
                            : "bg-[#00F0FF] !text-black shadow-[0_0_12px_rgba(0,240,255,0.3)]"
                          : isLight
                            ? "text-slate-700 hover:text-slate-900 hover:bg-white/80"
                            : "text-[#94A3B8] hover:text-white hover:bg-white/5"
                      }`}
                      data-testid={`filter-tf-${tf}`}
                    >
                      <span className={active ? (isLight ? "!text-white font-bold" : "!text-black font-bold") : (isLight ? "!text-slate-700 font-semibold" : "")}>
                        {tf === "ALL" ? "All TF" : TIMEFRAME_MAP[tf] || tf}
                      </span>
                    </button>
                  );
                })}
              </div>

              {/* Zone Type Cluster */}
              <div className={`flex items-center gap-1 p-1 rounded-xl border ${isLight ? "bg-slate-100/80 border-slate-200/80" : "bg-white/[0.02] border-white/[0.06]"}`}>
                <span className={`text-[10px] font-jetbrains uppercase font-bold px-2 hidden sm:inline-block ${isLight ? "text-slate-500" : "text-gray-400"}`}>Zone:</span>
                {["ALL", "DEMAND", "SUPPLY"].map(t => {
                  const active = filterType === t;
                  const activeBg = t === "DEMAND" 
                    ? (isLight ? "bg-emerald-600 !text-white" : "bg-[#00FF88] !text-black shadow-[0_0_12px_rgba(0,255,136,0.3)]")
                    : t === "SUPPLY"
                      ? (isLight ? "bg-rose-600 !text-white" : "bg-[#FF3B30] !text-white shadow-[0_0_12px_rgba(255,59,48,0.3)]")
                      : (isLight ? "bg-sky-600 !text-white" : "bg-white/20 !text-white");
                  
                  return (
                    <button
                      key={t}
                      onClick={() => setFilterType(t)}
                      className={`text-xs font-jetbrains h-7 px-2.5 rounded-lg transition-all flex items-center justify-center font-bold ${
                        active
                          ? `${activeBg} shadow-xs`
                          : isLight
                            ? "text-slate-700 hover:text-slate-900 hover:bg-white/80"
                            : "text-[#94A3B8] hover:text-white hover:bg-white/5"
                      }`}
                      data-testid={`filter-type-${t}`}
                    >
                      <span className={active ? (t === "DEMAND" && !isLight ? "!text-black font-bold" : "!text-white font-bold") : (isLight ? "!text-slate-700 font-semibold" : "")}>
                        {t === "ALL" ? "All Zones" : t}
                      </span>
                    </button>
                  );
                })}
              </div>

              {/* Near Entry Filter in Active mode */}
              {viewTab === "ACTIVE" && (
                <button
                  type="button"
                  onClick={() => setFilterStatus(prev => prev === "NEAR_ENTRY" ? "ALL" : "NEAR_ENTRY")}
                  className={`text-xs font-jetbrains h-7 px-2.5 rounded-xl border font-bold transition-all flex items-center gap-1 ${
                    filterStatus === "NEAR_ENTRY"
                      ? isLight ? "bg-amber-600 !text-white border-amber-600" : "bg-[#FFCC00]/20 text-[#FFCC00] border-[#FFCC00]/40 shadow-[0_0_10px_rgba(255,204,0,0.25)]"
                      : isLight ? "text-slate-600 hover:text-slate-900 border-slate-200" : "text-[#94A3B8] hover:text-white border-white/[0.06]"
                  }`}
                >
                  <Zap className="w-3 h-3 text-[#FFCC00]" />
                  <span>Near Entry Only</span>
                </button>
              )}
            </div>

            {/* Clear Filters button */}
            {(filterTF !== "ALL" || filterType !== "ALL" || filterStatus !== "ALL" || search) && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => { setFilterTF("ALL"); setFilterType("ALL"); setFilterStatus("ALL"); setSearch(""); }}
                className="text-[11px] h-7 px-2.5 text-zinc-400 hover:text-white"
              >
                Reset Filters
              </Button>
            )}
          </div>
        </div>

        {/* Informational Sub-header for historical tabs */}
        {viewTab === "TARGET_HIT" && (
          <div className="p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-xs text-emerald-400 flex items-center justify-between">
            <span className="flex items-center gap-2 font-medium">
              <Trophy className="w-4 h-4 text-emerald-400" />
              <span>Target Achieved Portfolio: Displaying completed trade setups that reached their profit target.</span>
            </span>
            <span className="font-mono text-[11px] font-bold">{filtered.length} Trades Hit Target</span>
          </div>
        )}
        {viewTab === "SL_HIT" && (
          <div className="p-3 rounded-xl bg-rose-500/10 border border-rose-500/20 text-xs text-rose-400 flex items-center justify-between">
            <span className="flex items-center gap-2 font-medium">
              <AlertCircle className="w-4 h-4 text-rose-400" />
              <span>Capital Protection Record: Trades that touched the stop-loss limit and were automatically closed.</span>
            </span>
            <span className="font-mono text-[11px] font-bold">{filtered.length} SL Hit</span>
          </div>
        )}
        {viewTab === "CLOSED" && (
          <div className="p-3 rounded-xl bg-purple-500/10 border border-purple-500/20 text-xs text-purple-400 flex items-center justify-between">
            <span className="flex items-center gap-2 font-medium">
              <Lock className="w-4 h-4 text-purple-400" />
              <span>Closed Trade Archive: Historical record of all completed trades.</span>
            </span>
            <span className="font-mono text-[11px] font-bold">{filtered.length} Closed Trades</span>
          </div>
        )}

        {/* Alert Cards */}
        {filtered.length === 0 ? (
          <div className="terminal-card rounded-2xl p-12 text-center bg-white dark:bg-black/40 border border-slate-200 dark:border-white/[0.08] shadow-sm">
            <BarChart3 className={`w-10 h-10 mx-auto mb-3 ${isLight ? "text-slate-400" : "text-gray-500"}`} />
            <p className={`font-outfit font-bold text-base ${isLight ? "!text-slate-900" : "text-white"}`}>
              {viewTab === "ACTIVE" 
                ? "No active trades match current filters" 
                : viewTab === "TARGET_HIT" 
                ? "No target hit trades recorded yet" 
                : "No completed trades in this category"}
            </p>
            <p className={`text-xs font-plex mt-1.5 ${isLight ? "text-slate-600 font-medium" : "text-gray-400"}`}>
              {viewTab === "ACTIVE" 
                ? "New actionable trades from Drishti AI will appear here live in real-time." 
                : "Historical trades will be archived here once closed."}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="alerts-grid">
            {filtered.map(alert => (
              <AlertCard
                key={alert.alert_id || alert.id}
                alert={alert}
                livePrice={livePrices[alert.script_name]}
                onTradeClick={handleTradeClick}
                onDeleteClick={handleDeleteAlert}
                isAdmin={isAdmin}
                onEditClick={(a) => setEditModalAlert(a)}
                onCloseClick={(a, lp) => setCloseModalAlert({ ...a, cmp: lp?.last_price || a.cmp })}
                onReopenClick={handleReopenAlert}
              />
            ))}
          </div>
        )}
      </div>

      {/* Admin only: Connect Dhan + Execution modal */}
      {isAdmin && (
        <>
          <ConnectDhanModal
            open={connectModalOpen}
            onOpenChange={setConnectModalOpen}
            onConnected={() => { setConnectModalOpen(false); setDhanConnected(true); }}
          />
          <TradeExecutionModal
            open={tradeModalOpen}
            onOpenChange={setTradeModalOpen}
            alert={selectedAlert}
            livePrice={selectedLivePrice}
          />
          <EditAlertModal
            isOpen={!!editModalAlert}
            alert={editModalAlert}
            onClose={() => setEditModalAlert(null)}
            onSuccess={(updated) => {
              const uId = updated.alert_id || updated.id;
              setAlerts(prev => prev.map(a => (a.alert_id || a.id) === uId ? { ...a, ...updated } : a));
              fetchAlerts();
            }}
          />
          <CloseTradeModal
            isOpen={!!closeModalAlert}
            alert={closeModalAlert}
            livePrice={closeModalAlert?.cmp}
            onClose={() => setCloseModalAlert(null)}
            onSuccess={(updated) => {
              const uId = updated.alert_id || updated.id;
              setAlerts(prev => prev.map(a => (a.alert_id || a.id) === uId ? { ...a, ...updated, is_closed: true } : a));
              fetchAlerts();
            }}
          />
        </>
      )}

      {/* All users: Alert Info Panel (read-only manual trading guide) */}
      <AlertInfoModal
        open={infoModalOpen}
        onOpenChange={setInfoModalOpen}
        alert={selectedAlert}
        livePrice={selectedLivePrice}
        capacityData={capacityData}
      />
    </DashboardLayout>
  );
};

function formatPrice(num) {
  if (num == null || num === 0) return "—";
  return Number(num).toLocaleString("en-IN", { maximumFractionDigits: 2 });
}

function getTimeAgo(dateStr) {
  if (!dateStr) return "";
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

export default DrishtiAlertsPage;
